import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { edgeErrorMessage } from "@/lib/edge-errors";
import type { LeadSegment } from "@/lib/db-types";

interface SegmentSearchResponse {
  total?: number;
  total_in_register?: number;
  fetched?: number;
  truncated?: boolean;
  inserted?: number;
  skipped_customer?: number;
  skipped_duplicate?: number;
  error?: string;
  skipped?: string;
}

async function invokeSegmentSearch(body: Record<string, unknown>): Promise<SegmentSearchResponse> {
  const { data, error } = await supabase.functions.invoke<SegmentSearchResponse>("brreg-segment-search", { body });
  if (error) throw new Error(await edgeErrorMessage(error, data, "Søket mot Brønnøysund feilet"));
  if (data?.skipped === "flag_off") throw new Error("Leads er ikke skrudd på ennå");
  if (data?.error) throw new Error(data.error);
  return data ?? {};
}

/** Forhåndsvisning: antall treff i Enhetsregisteret for segmentet. */
export function useSegmentSearchCount() {
  return useMutation({
    mutationFn: async (segment: LeadSegment): Promise<number> => (await invokeSegmentSearch({ segment, mode: "count" })).total ?? 0,
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Søker og importerer treffene i en eksisterende Brønnøysund-liste. */
export function useSegmentSearchImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listId: string; segment: LeadSegment }) =>
      invokeSegmentSearch({ list_id: input.listId, segment: input.segment }),
    onSuccess: async () => {
      await invalidateFor(qc, "leads");
      await invalidateFor(qc, "lead_lists");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
